import produce from 'immer';
import create, { StateCreator } from 'zustand';

import companyApi from 'lib/api/company';

import { CorpActionReduxState } from './CorpAction/types';
import { HoldingCompositionState, KeystatsPayload } from './slice.types';

const holdingCompositionInitialState: HoldingCompositionState = {
  data: {},
  error: {},
  isLoading: false,
};
const corpActionInitialState: CorpActionReduxState = {};

// Initial State
const initialState = {
  profile: {},
  holdingComposition: holdingCompositionInitialState,
  corpAction: corpActionInitialState,
  consensus: {},
  insider: {},
  seasonality: {},
  keystat: {},
  financial: {},
  analyst: {},
  analystConsensus: {},
  isLoading: false,
  isLoadingAnalystConsensus: false,
  message: '',
  error: null,
  financialParams: {
    report: 1,
    statement: 1,
  },
  pinnedPost: {
    data: {},
    isLoading: {},
    message: {},
    error: {},
  },
};

type State = typeof initialState;

export interface Actions {
  setLoading: (isLoading: boolean) => void;
  getCompanyKeystat: (symbol: string) => any;
}

const companyActions: StateCreator<State & Actions, [], [], Actions> = (
  set
) => ({
  setLoading: (value) => {
    set((state) =>
      produce(state, (draft) => {
        draft.isLoading = value;
      }),
    );
  },
  getCompanyKeystat: async (symbol: string) => {
    try {
      set((state) =>
        produce(state, (draft) => {
          draft.isLoading = true;
        })
      );
      const getKeystat = await companyApi.getCompanyKeystat(symbol);

      if (!getKeystat.data) {
        throw new Error('Attempt to get company keystat failed');
      }

      const { error_type: error, data, message } = getKeystat.data;

      if (error) {
        set((state) =>
          produce(state, (draft) => {
            draft.error = error;
            draft.message = message
          })
        );
        return { error, message };
      }

      set((state) =>
        produce(state, (draft) => {
          draft.keystat[symbol] = data;
        })
      );
      
      return {
        data,
        symbol,
      };
    } catch (error) {
      set((state) =>
        produce(state, (draft) => {
          draft.error = error;
        })
      );
      return { error };
    } finally {
      set((state) =>
        produce(state, (draft) => {
          draft.isLoading = false;
        })
      );
    }
  }
});

export type CompanyStore = Actions & State;

export const useCompanyStore = create<CompanyStore>(
  (set, get, store, storeMutation) => ({
    ...initialState,
    ...companyActions(set, get, store, storeMutation),
  }),
);